import useNotification from '@/hooks/useNotification';
import { EDITORIAL_TAG_ID } from '@/utils/constants';
import { trpc } from '@/utils/trpc';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import 'lazysizes';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAdmin from '../../hooks/useAdmin';
import useHover from '../../hooks/useHover';
import { trialActive } from '../../services/user';
import { searchTermAtom, uiColors } from '../../store/uiStore';
import { ownedClipsAtom, ownedPhotosAtom } from '../../store/userStore';
import AdminEditButton from './AdminEditButton';
import BookmarkButton from './BookmarkButton';
import DownloadMediaButtonContainer from './DownloadMediaButtonContainer';
import ModalContainer from './ModalContainer';
import QuickDownloadButton from './QuickDownloadButton';
import UpgradeModalFlow from './UpgradeModalFlow';
import VimeoPlayer from './VimeoPlayer';
import useIsEditorial from '../../hooks/useIsEditorial';
import SocialLinkFrame from '../moodboard/SocialLinkFrame';
import LinkHoverButtons from '../moodboard/LinkHoverButtons';
import { getMediaType, MediaType } from '@/utils/mediaHelpers';
import useInApp from '@/hooks/useInApp';
import BigDownloadButton from './atoms/BigDownloadButton';
import { social_links } from '@prisma/client';
import { NoteState } from '../layout/StandardPhotoGrid';
import DTSAssetEditButton from '../moodboard/DTSAssetEditButton';
import useBoardDuplication from '@/hooks/useBoardDuplication';
import EditorialButton from './EditorialButton';
import RankingScore from './RankingScore';
import MediaHoverLink from '../iq/MediaHoverLink';

interface MediaViewerProps {
  media: any;
  cover?: boolean;
  controls?: boolean;
  showBookmarkButton?: boolean;
  showQuickDownload?: boolean;
  showBigDownloadButton?: boolean;
  showVideoRuntime?: boolean;
  align?: string;
  bgColor?: boolean;
  allowSingleModalView?: boolean;
  actualDimensions?: boolean;
  lazyLoad?: boolean;
  videoMuted?: boolean;
  videoAutoPlay?: boolean;
  download?: boolean;
  onEditLink?: (id: number) => void;
  fixedHeightMedia?: number;
  noteState?: NoteState;
  onAddNote?: (media) => void;
  displayNote?: boolean;
  showRankingScore?: boolean;
  showTrendButton?: boolean;
}

const MediaViewer = ({
  media,
  cover = true,
  controls = false,
  showBookmarkButton = true,
  showQuickDownload = true,
  showBigDownloadButton = false,
  showVideoRuntime = true,
  align = 'center',
  bgColor = true,
  allowSingleModalView = true,
  actualDimensions = false,
  lazyLoad = true,
  videoMuted = true,
  videoAutoPlay = false,
  download = false,
  onEditLink = (id: number) => {},
  fixedHeightMedia = 0,
  noteState,
  onAddNote,
  displayNote,
  showRankingScore = false,
  showTrendButton = false,
}: MediaViewerProps) => {
  const { inPublicMoodbard } = useInApp();
  const [searchTerm] = useAtom(searchTermAtom);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const isHovered = useHover(containerRef);
  const [sizedUrl, setSizedUrl] = useState<string>();
  const [sizedBookmarkButton] = useState('large');
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [bookmarkPopoutActive, setBookmarkPopoutActive] = useState(false);
  const { showNotification } = useNotification();
  const toggleTagMutation = trpc.photos.toggleTag?.useMutation();
  const toggleClipTagMutation = trpc.packs.toggleClipTag?.useMutation();

  const [upgradeModal, setUpgradeModal] = useState(false);
  const { data: user } = trpc.users.getAuthenticatedUser.useQuery(undefined, {
    staleTime: Infinity,
  });

  const mediaType = useMemo<MediaType>(() => {
    return getMediaType(media);
  }, [media]);

  const links = useMemo(() => {
    if (inPublicMoodbard) {
      return { href: '/public/protected', adminHref: '' };
    }
    if (media?.type === 'video') {
      return { href: `/project/${media?.id}`, adminHref: `projects/${media?.id}` };
    } else if (media?.type === 'clip') {
      try {
        const pack = JSON.parse(media.pack);
        return {
          href: `/clip/${media?.id}`,
          adminHref: `projects/${pack.id}`,
          trendUrl: `/admin/dashboard/trend/clip/${media?.id}`,
          relativeScoreUrl: `/admin/dashboard/relative-score/clip/${media?.id}`,
        };
      } catch (error) {
        return {
          href: `/clip/${media?.id}`,
          adminHref: `projects/${media?.pack_id ?? ''}`,
          trendUrl: `/admin/dashboard/trend/clip/${media?.id}`,
          relativeScoreUrl: `/admin/dashboard/relative-score/clip/${media?.id}`,
        };
      }
    } else {
      if (media?.name?.includes('DTS_Echos')) {
        //Temporary way to identify echos photo as it is a experimental feature
        return {
          href: `/echos/${media?.id}`,
          adminHref: `echos`,
          trendUrl: `/admin/dashboard/trend/photo/${media?.id}`,
          relativeScoreUrl: `/admin/dashboard/relative-score/photo/${media?.id}`,
        };
      }
      return {
        href: `/photo/${media?.id}`,
        adminHref: `visuals/${media?.id}`,
        trendUrl: `/admin/dashboard/trend/photo/${media?.id}`,
        relativeScoreUrl: `/admin/dashboard/relative-score/photo/${media?.id}`,
      };
    }
  }, [media]);

  useEffect(() => {
    function seuptContainerDimensions() {
      //get width of container
      const wrapperWidth = containerRef?.current?.offsetWidth ?? 0;
      const wrapperHeight = containerRef?.current?.offsetHeight ?? 0;

      const containerRatio = wrapperWidth / wrapperHeight;

      const dim = media?.dimensions?.split('x');

      const mediaRatio = dim ? dim[0] / dim[1] : 1.777777;

      setContainerWidth(mediaRatio > containerRatio ? wrapperWidth : wrapperHeight * mediaRatio);
      setContainerHeight(mediaRatio > containerRatio ? wrapperWidth / mediaRatio : wrapperHeight);
    }

    seuptContainerDimensions();

    window.addEventListener('resize', seuptContainerDimensions);
    return () => {
      window.removeEventListener('resize', seuptContainerDimensions);
    };
  }, [media, imageLoaded]);

  useEffect(() => {
    setSizedUrl(containerWidth < 330 ? 'small_image_url' : 'large_image_url');
  }, [containerWidth]);

  const handleUpgradeDownloadClick = (e) => {
    e.stopPropagation();
    setUpgradeModal(true);
  };

  const router = useRouter();
  const [ownedPhotos] = useAtom(ownedPhotosAtom);
  const [ownedClips] = useAtom(ownedClipsAtom);

  const owned = useMemo(() => {
    if (mediaType === 'photo') {
      return media?.id && ownedPhotos?.includes(media?.id);
    } else if (mediaType === 'clip') {
      return media?.id && ownedClips?.includes(media?.id);
    } else {
      return false;
    }
  }, [ownedPhotos, media, mediaType, ownedClips]);

  const showOwnedChainlink =
    (router.pathname === '/photo/[id]' || router.pathname === '/clip/[id]') && owned;
  const showAdmin = useAdmin();

  const { isEditorial, refetch } = useIsEditorial(
    media?.id!,
    mediaType,
    media.id && media?.id > 0 && showAdmin
  );

  const toggleTag = async () => {
    try {
      showNotification(
        {
          message: `${isEditorial ? 'Removing' : 'Adding'} the Editorial tag.`,
        },
        6000
      );

      if (mediaType === 'photo') {
        await toggleTagMutation.mutateAsync({ assetId: media?.id, tagId: EDITORIAL_TAG_ID });
      } else if (mediaType === 'clip') {
        await toggleClipTagMutation.mutateAsync({ clip_id: media?.id, tag_id: EDITORIAL_TAG_ID });
      }
      await refetch();
    } catch (e: unknown) {
      await refetch();

      showNotification(
        {
          message: `There was an issue adding the Editorial tag. Please try again.`,
          emoji: '⚠️',
        },
        6000
      );
    }
  };

  const fixedHeight = fixedHeightMedia > 0 ? `h-[${fixedHeightMedia}px]` : 'h-full';

  const currentNoteState = useMemo(() => {
    return noteState ? noteState[media.bookmarkId] : undefined;
  }, [noteState, media]);

  const { setShowOwnershipTipModal, isOwnerOfCollection } = useBoardDuplication();

  const handleEditLinkClick = useCallback(() => {
    if (!isOwnerOfCollection) {
      setShowOwnershipTipModal(true);
      return;
    }
    if (currentNoteState?.showNote && onAddNote) {
      onAddNote(media);
    } else {
      onEditLink(media.bookmarkId);
    }
  }, [media, currentNoteState, isOwnerOfCollection, setShowOwnershipTipModal]);

  // Get a random background color from uiColors array
  const randomBgColor = useMemo(() => {
    if (!uiColors || uiColors.length === 0) return '';

    // Filter out black and white colors
    const filteredColors = uiColors.filter(
      (color) => color.name.toUpperCase() !== '#000000' && color.name.toUpperCase() !== '#FFFFFF'
    );

    if (filteredColors.length === 0) return '';

    const randomIndex = Math.floor(Math.random() * filteredColors.length);
    return filteredColors[randomIndex].name;
  }, []);

  const shouldShowRankingScore = useMemo(() => {
    return showRankingScore && showAdmin && (mediaType === 'photo' || mediaType === 'clip');
  }, [showRankingScore, showAdmin, mediaType]);

  const shouldShowTrendButton = useMemo(() => {
    return showTrendButton && showAdmin && (mediaType === 'photo' || mediaType === 'clip');
  }, [showTrendButton, showAdmin, mediaType]);

  return (
    <div
      ref={containerRef}
      className={clsx(['w-full flex items-center justify-center group'], fixedHeight)}
    >
      <div
        className="relative cursor-pointer max-w-full max-h-full"
        style={{
          backgroundColor: bgColor ? media?.dominant_hex_code : '',
          width: actualDimensions ? containerWidth + 'px' : '100%',
          height: actualDimensions ? containerHeight + 'px' : '100%',
        }}
      >
        {shouldShowRankingScore && <RankingScore media={{ ...media, type: mediaType }} />}
        {shouldShowTrendButton && <MediaHoverLink text="Trend" url={links.trendUrl ?? ''} />}
        {shouldShowTrendButton && (
          <MediaHoverLink
            text="Relative"
            url={links.relativeScoreUrl ?? ''}
            position="bottom-10 right-0"
          />
        )}
        {mediaType === 'link' && (
          <>
            <div className="relative w-full h-full group/link">
              <SocialLinkFrame link={media} />
              <div
                className={clsx([
                  currentNoteState?.showNote
                    ? 'opacity-100'
                    : 'opacity-0 group-hover/link:opacity-100',
                ])}
              >
                <LinkHoverButtons
                  onEditClick={handleEditLinkClick}
                  link={media as social_links & { bookmarkId: number }}
                  currentNoteState={currentNoteState}
                />
              </div>
            </div>
          </>
        )}

        {mediaType === 'photo' && sizedUrl && (
          <>
            {allowSingleModalView && (
              <>
                <Link href={`${links.href}${searchTerm ? '?searchTerm=' + searchTerm : ''}`}>
                  {/* eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text */}
                  <img
                    ref={imageRef}
                    key={media?.imgix?.links?.[sizedUrl]}
                    data-src={media?.imgix?.links?.[sizedUrl]}
                    src={lazyLoad ? '' : media?.imgix?.links?.[sizedUrl]}
                    onLoad={() => {
                      setImageLoaded(true);
                    }}
                    className={clsx(
                      'pointer-events-none',
                      lazyLoad && 'lazyload',
                      cover ? 'object-cover w-full h-full' : 'object-contain',
                      align === 'top' ? 'object-top' : 'object-center'
                    )}
                  />
                </Link>
                {displayNote && currentNoteState?.showNote && !inPublicMoodbard && (
                  <DTSAssetEditButton
                    onEditClick={onAddNote ? () => onAddNote(media) : undefined}
                  />
                )}
                {download && (
                  <DownloadMediaButtonContainer
                    media={media}
                    resolution={'print'}
                    hoverShow={false}
                    className={'absolute inset-0 z-1'}
                  >
                    <></>
                  </DownloadMediaButtonContainer>
                )}
                {(imageRef.current?.offsetWidth || 0) > 140 &&
                  (showBookmarkButton || showQuickDownload) &&
                  !download && (
                    <div className="bg-white text-red-400 rounded-[50%] text-center flex items-center justify-center w-[80px] h-[36px] uppercase tracking-[0.15em] text-[14px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] pointer-events-none opacity-0 group-hover:opacity-100">
                      View
                    </div>
                  )}
                {showBigDownloadButton && <BigDownloadButton />}
              </>
            )}
            {!allowSingleModalView && !!media?.imgix?.links && (
              /* eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text */
              <img
                ref={imageRef}
                key={media?.imgix.links[sizedUrl]}
                data-src={media?.imgix.links[sizedUrl]}
                src={lazyLoad ? '' : media?.imgix.links[sizedUrl]}
                onLoad={() => {
                  setImageLoaded(true);
                }}
                className={clsx([
                  'pointer-events-none',
                  lazyLoad && 'lazyload',
                  cover ? 'object-cover w-full h-full' : 'object-contain',
                  align === 'top' ? 'object-top' : 'object-center',
                ])}
              />
            )}
          </>
        )}

        {(mediaType === 'video' || mediaType === 'clip') && (
          <div
            className="relative h-full group/clip"
            style={{
              backgroundColor: imageLoaded ? undefined : randomBgColor,
              width: actualDimensions ? containerWidth + 'px' : '100%',
              height: actualDimensions ? containerHeight + 'px' : '100%',
            }}
          >
            {showBookmarkButton && (
              <div
                className={clsx([
                  'absolute z-10 top-0 right-0 grid gap-2',
                  sizedBookmarkButton === 'small' ? 'p-3' : 'p-4',
                ])}
              >
                {showAdmin && <EditorialButton isEditorial={isEditorial} toggleTag={toggleTag} />}
                <BookmarkButton
                  media={{ ...media, type: mediaType }}
                  shouldShow={isHovered}
                  size={sizedBookmarkButton}
                  onClick={setBookmarkPopoutActive}
                />
                {showAdmin && (
                  <div className={isHovered ? 'opacity-1000' : 'opacity-0'}>
                    <AdminEditButton
                      adminPath={links.adminHref}
                      isEditorial={media?.tags?.find((tag) => tag.name === '5343')}
                    />
                  </div>
                )}
                {trialActive(user) && !owned ? (
                  <div
                    className={clsx([isHovered ? 'flex' : 'hidden'])}
                    onClick={(e) => handleUpgradeDownloadClick(e)}
                  >
                    <QuickDownloadButton size={sizedBookmarkButton} />
                  </div>
                ) : (
                  <DownloadMediaButtonContainer
                    media={media}
                    hoverShow={isHovered}
                    resolution="web"
                  >
                    <QuickDownloadButton size={sizedBookmarkButton} />
                  </DownloadMediaButtonContainer>
                )}
              </div>
            )}
            {allowSingleModalView && (
              <Link href={links.href}>
                <VimeoPlayer
                  videoUrl={media?.video_url?.toLowerCase()}
                  muted={videoMuted}
                  controls={controls}
                  showVideoRuntime={showVideoRuntime}
                  videoAutoPlay={videoAutoPlay}
                  fitByWidth={mediaType === 'clip' ? media?.orientation === 'landscape' : undefined}
                  onReady={() => {
                    setImageLoaded(true);
                  }}
                />
              </Link>
            )}
            {showBigDownloadButton && <BigDownloadButton />}
            {displayNote && currentNoteState?.showNote && !inPublicMoodbard && (
              <DTSAssetEditButton onEditClick={onAddNote ? () => onAddNote(media) : undefined} />
            )}
          </div>
        )}

        {showOwnedChainlink && (
          <Image
            className="absolute bottom-2 right-2"
            src="/assets/images/chainlink.png"
            alt="Owned asset chainlink symbol"
            width={50}
            height={50}
          />
        )}
        {imageLoaded &&
          sizedBookmarkButton &&
          (showBookmarkButton || showQuickDownload) &&
          !download && (
            <div
              className={clsx([
                'absolute z-10',
                bookmarkPopoutActive ? 'z-40' : '',
                'grid gap-2',
                sizedBookmarkButton === 'small' ? 'p-3' : 'p-4',
              ])}
              style={{
                top: '0px',
                right: '0px',
              }}
            >
              {showAdmin && <EditorialButton isEditorial={isEditorial} toggleTag={toggleTag} />}

              {showQuickDownload &&
                (trialActive(user) && !owned ? (
                  <div
                    className={clsx([isHovered ? 'flex' : 'hidden'])}
                    onClick={(e) => handleUpgradeDownloadClick(e)}
                  >
                    <QuickDownloadButton size={sizedBookmarkButton} />
                  </div>
                ) : (
                  <DownloadMediaButtonContainer
                    media={media}
                    hoverShow={isHovered}
                    resolution="web"
                  >
                    <QuickDownloadButton size={sizedBookmarkButton} />
                  </DownloadMediaButtonContainer>
                ))}

              {showBookmarkButton && (
                <BookmarkButton
                  media={{ ...media, type: mediaType }}
                  shouldShow={isHovered}
                  size={sizedBookmarkButton}
                  onClick={setBookmarkPopoutActive}
                />
              )}

              {showAdmin && (
                <div className={isHovered ? 'opacity-1000' : 'opacity-0'}>
                  <AdminEditButton
                    adminPath={links.adminHref}
                    isEditorial={media?.tags?.find((tag) => tag.name === '5343')}
                  />
                </div>
              )}
            </div>
          )}
      </div>

      {upgradeModal && (
        <ModalContainer onClose={() => setUpgradeModal(false)}>
          <UpgradeModalFlow onClose={() => setUpgradeModal(false)} />
        </ModalContainer>
      )}
    </div>
  );
};

export default MediaViewer;
